import React, { useMemo, useState } from "react";
import { OpenWindowConfig } from "../windowManager/WindowManagerContext";
import { I } from "../redesign/kvpIcons";
import { Toast } from "../redesign/Toast";
import { useMailContext } from "../../context/MailContext";
import { canReadMailbox } from "../../utils/mailboxAccess";
import {
    mailVorgangRegelService,
    type MailVorgangRegelDto,
} from "../../services/mailVorgangRegelService";
import { MailRegelForm } from "./MailRegelForm";
import { MailRegelSection } from "./MailRegelSection";
import {
    entwurfAlsEingabe,
    entwurfAusRegel,
    leererEntwurf,
    validiereRegel,
    vorbelegungAusMail,
    type RegelEntwurf,
    type RegelZielVorgang,
} from "./mailRegel";
import "./mailSettings.css";
import { reportMailError } from "../../services/mailError";

/**
 * Das Fenster „Mail-Regel" (Cluster REGELN, G4/H5) — EINZIGER Einstiegspunkt
 * für alle vier Wege: Rechtsklick in der Mailliste, Lesefenster, Vorgang und
 * der Reiter „Regeln" in den Mail-Einstellungen.
 *
 * Unter dem Formular steht IMMER die passende Verwaltungsliste: ohne fixierten
 * Vorgang die eigenen Regeln (damit man keine Dublette anlegt), mit fixiertem
 * Vorgang ALLE Regeln dieses Vorgangs — auch die von Kolleginnen.
 */

export type OpenMailRegelParams = {
    /** Vorbelegung aus der angeklickten Mail. */
    vorbelegung?: {
        senderEmail?: string | null;
        subject?: string | null;
        mailbox?: string | null;
    };
    /** Aus einem Vorgang heraus geöffnet: das Ziel steht fest. */
    vorgang?: RegelZielVorgang | null;
    /** Rückmeldung an den Aufrufer, wenn gespeichert wurde. */
    onSaved?: () => void;
};

const MailRegelContent: React.FC<OpenMailRegelParams> = ({ vorbelegung, vorgang, onSaved }) => {
    const { mailboxes } = useMailContext();

    const postfaecher = useMemo(
        () =>
            (mailboxes ?? [])
                .filter((m) => canReadMailbox(m))
                .map((m) => m.mailboxAddress)
                .filter(Boolean),
        [mailboxes],
    );

    const [entwurf, setEntwurf] = useState<RegelEntwurf>(() =>
        vorbelegung || vorgang
            ? vorbelegungAusMail({ ...(vorbelegung ?? {}), vorgang: vorgang ?? null })
            : leererEntwurf(),
    );
    /** Gesetzt = bestehende Regel bearbeiten statt neu anlegen. */
    const [bearbeiteId, setBearbeiteId] = useState<number | null>(null);
    const [speichert, setSpeichert] = useState(false);
    const [meldung, setMeldung] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [toast, setToast] = useState<{ msg: string; tone: "error" | "success" } | null>(null);

    const problem = validiereRegel(entwurf);

    const zuruecksetzen = () => {
        setBearbeiteId(null);
        setEntwurf(
            vorbelegung || vorgang
                ? vorbelegungAusMail({ ...(vorbelegung ?? {}), vorgang: vorgang ?? null })
                : leererEntwurf(),
        );
        setMeldung(null);
    };

    const speichern = async () => {
        if (problem || speichert) return;
        setSpeichert(true);
        setMeldung(null);
        try {
            const body = entwurfAlsEingabe(entwurf);
            if (bearbeiteId === null) {
                await mailVorgangRegelService.create(body);
                setToast({ msg: "Regel angelegt.", tone: "success" });
            } else {
                await mailVorgangRegelService.update(bearbeiteId, body);
                setToast({ msg: "Regel gespeichert.", tone: "success" });
            }
            zuruecksetzen();
            setReloadKey((k) => k + 1);
            onSaved?.();
        } catch (error: unknown) {
            setMeldung(reportMailError({
                area: "mail",
                action: "save-settings",
                error,
                fallbackMessage: "Die Regel konnte nicht gespeichert werden.",
            }));
        } finally {
            setSpeichert(false);
        }
    };

    const bearbeiten = (r: MailVorgangRegelDto) => {
        setBearbeiteId(r.id);
        setEntwurf(entwurfAusRegel(r, vorgang?.label ?? null));
        setMeldung(null);
    };

    return (
        <div
            className="mset"
            style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                // Explizit weiß: der Fenster-Body des WindowManagers ist grau.
                background: "#fff",
            }}
        >
            <div style={{ flex: 1, overflowY: "auto", padding: 16, minHeight: 0 }}>
                <MailRegelForm
                    entwurf={entwurf}
                    onChange={setEntwurf}
                    vorgangFixiert={vorgang ?? null}
                    postfaecher={postfaecher}
                    betreffQuelle={vorbelegung?.subject ?? null}
                />

                <MailRegelSection
                    quelle={vorgang ? "vorgang" : "eigene"}
                    vorgangId={vorgang?.id ?? null}
                    vorgangLabel={vorgang?.label ?? null}
                    reloadKey={reloadKey}
                    onEdit={bearbeiten}
                />
            </div>

            <div
                style={{
                    flexShrink: 0,
                    borderTop: "1px solid var(--mset-line-2)",
                    padding: "10px 16px",
                    background: "#faf7fa",
                }}
            >
                {(meldung || problem) && (
                    <div
                        className="mset-hint"
                        style={{ color: meldung ? "var(--mset-err)" : undefined, marginTop: 0 }}
                    >
                        {meldung ?? problem}
                    </div>
                )}
                <div className="mset-actions" style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        className="mset-btn primary"
                        disabled={!!problem || speichert}
                        onClick={() => void speichern()}
                    >
                        {bearbeiteId === null ? "Regel anlegen" : "Änderungen speichern"}
                    </button>
                    <button
                        type="button"
                        className="mset-btn"
                        disabled={speichert}
                        onClick={zuruecksetzen}
                    >
                        {bearbeiteId === null ? "Formular leeren" : "Bearbeiten abbrechen"}
                    </button>
                </div>
            </div>

            <Toast
                open={!!toast}
                message={toast?.msg ?? ""}
                tone={toast?.tone}
                onClose={() => setToast(null)}
            />
        </div>
    );
};

/**
 * Öffnet das Regel-Fenster (Muster `openMailSettingsWindow`).
 *
 * Die Fenster-Id ist bewusst NICHT stabil: zwei Mails nebeneinander sollen zwei
 * Regel-Entwürfe erlauben — anders als die Einstellungen, die es nur einmal gibt.
 */
export const openMailRegelWindow = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenMailRegelParams = {},
): string => {
    const id = `mail-regel-${Date.now()}`;
    openWindow({
        id,
        title: "Mail-Regel",
        icon: I.settings,
        initialSize: { width: 760, height: 680 },
        center: true,
        render: () => <MailRegelContent {...params} />,
    });
    return id;
};

export default MailRegelContent;
